/**
 * The web app's door into the contractor books database, for signed-in
 * members only.
 *
 * Supabase fronts Postgres with PostgREST and verifies Firebase ID tokens
 * (third-party auth), so every request carries the member's own token as
 * the bearer and the project's PUBLISHABLE key as `apikey` — the gateway's
 * ticket, which holds no grant of its own (plan D8). Row-level security and
 * the `security definer` functions do the rest on the server.
 *
 * A thin fetch client rather than `@supabase/supabase-js`, the same shape as
 * `functions/shared/booksDb.js`: reads of tables and views, inserts and
 * updates of the plain rows a member may touch, and RPC for everything
 * money-bearing. One fewer dependency, and one error type across both
 * sides: a `raise exception` from a books function arrives as
 * `{ message: 'books.<code>', details: '<json>' }` and becomes a
 * `BooksError` whose `code` the caller switches on.
 *
 * A client must not exist before the claims are stamped
 * (`waitForBooksClaims`): a token without `role: 'authenticated'` is
 * rejected by the gateway. `createBooksClient` therefore takes a
 * `getToken`, and `booksSession.js` is the only place that calls it.
 *
 * An admin's read-only view of ONE organization (phase 3d §7) is a client
 * made with `adminView: <organization id>`: it sends
 * `x-books-admin-view: <id>` on every request, and the database answers
 * `viewer` for that organization alone to a token carrying the admin claim
 * (`member_role`). The header belongs to the client INSTANCE, never to the
 * module: the admin's own desk is a client made without it.
 */

export class BooksError extends Error {
  constructor(code, message, detail) {
    super(message);
    this.name = 'BooksError';
    this.code = code;
    this.detail = detail;
  }
}

/**
 * The local books gateway's publishable key — the same string as
 * `supabase/local/keys.cjs`, which this file cannot import from outside
 * `src/` (`supabase/tests/local-gateway.test.mjs` fails if they drift).
 */
export const LOCAL_BOOKS_ANON_KEY = 'local-books-anon-key';

let warnedNoLocalPort = false;

/**
 * Isolated emulator mode: the local books gateway on this machine
 * (`supabase/local/serve.mjs`), whatever `REACT_APP_BOOKS_URL` says — the
 * env file carries the REAL books address, and a page on the Auth emulator
 * must never reach it. Without the gateway's port the books stay off.
 */
function localBooksConfig(env) {
  const port = Number(env.REACT_APP_BOOKS_EMULATOR_PORT);
  if (!Number.isInteger(port) || port <= 0) {
    if (!warnedNoLocalPort) {
      warnedNoLocalPort = true;
      console.error(
        '[books] isolated emulator mode without REACT_APP_BOOKS_EMULATOR_PORT: the books stay off rather than reach the real books database. Start the app with `npm run start:dev:emulators`.'
      );
    }
    return null;
  }
  return { url: `http://127.0.0.1:${port}`, key: LOCAL_BOOKS_ANON_KEY };
}

/** `{ url, key }` from the env, or null when the books are not configured. */
export const booksConfig = (env = process.env) => {
  if (env.REACT_APP_USE_EMULATORS === 'true') return localBooksConfig(env);
  const url = (env.REACT_APP_BOOKS_URL || '').replace(/\/+$/, '');
  const key = env.REACT_APP_BOOKS_ANON_KEY || '';
  return url && key ? { url, key } : null;
};

async function toError(res) {
  let body = {};
  try {
    body = await res.json();
  } catch {
    // no JSON body; the status alone will do
  }
  const message = body.message || `${res.status} ${res.statusText}`;
  const code = message.startsWith('books.')
    ? message
    : `books/http-${res.status}`;
  let detail = body.details ?? null;
  if (typeof detail === 'string') {
    try {
      detail = JSON.parse(detail);
    } catch {
      // the detail stays a string
    }
  }
  return new BooksError(code, message, detail);
}

/**
 * @param {Object} opts
 * @param {() => Promise<string>} opts.getToken  the member's Firebase ID token
 * @param {{url: string, key: string}} [opts.config]  defaults to the env
 * @param {typeof fetch} [opts.fetchImpl]
 * @param {string} [opts.adminView]  the organization an admin views, read-only
 */
export function createBooksClient({
  getToken,
  config = booksConfig(),
  fetchImpl = (...args) => fetch(...args),
  adminView,
}) {
  if (!config) {
    throw new BooksError(
      'books/not-configured',
      'REACT_APP_BOOKS_URL and REACT_APP_BOOKS_ANON_KEY must both be set'
    );
  }
  if (typeof getToken !== 'function') {
    throw new BooksError('books/no-token', 'createBooksClient needs getToken');
  }
  if (
    adminView !== undefined &&
    (typeof adminView !== 'string' || !adminView.trim())
  ) {
    throw new BooksError(
      'books/bad-admin-view',
      'an admin view names the organization it reads'
    );
  }
  const { url, key } = config;
  const viewHeaders = adminView ? { 'x-books-admin-view': adminView } : {};

  async function request(method, path, { params, body, prefer } = {}) {
    const token = await getToken();
    const qs = params ? new URLSearchParams(params).toString() : '';
    const headers = {
      apikey: key,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...viewHeaders,
    };
    if (prefer) headers.Prefer = prefer;
    const res = await fetchImpl(`${url}/rest/v1/${path}${qs ? `?${qs}` : ''}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) throw await toError(res);
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  }

  return {
    /** Rows of a table or view. `params` are PostgREST query params. */
    select: (relation, params = {}) => request('GET', relation, { params }),
    /** Insert one row (or several) and return what was written. */
    insert: (relation, rows) =>
      request('POST', relation, {
        body: rows,
        prefer: 'return=representation',
      }),
    /** Update the rows `params` select and return them. */
    update: (relation, params, patch) =>
      request('PATCH', relation, {
        params,
        body: patch,
        prefer: 'return=representation',
      }),
    /** Call a SQL function with named arguments; null for `void`. */
    rpc: (fn, args = {}) => request('POST', `rpc/${fn}`, { body: args }),
  };
}
